/**
 * AgentFederationOverrideSlideout — Stage 3c-4 (P0-IDENT).
 *
 * Edit form for an agent-level federation peer override. Narrows or
 * replaces the resolved peer set for a specific agent — useful when
 * a single agent should talk to a subset of the tenant/instance/org
 * peer list.
 *
 * Loads the available peers from /api/v1/identity/federation-peers
 * (Stage 3d controller) and renders a multi-select. PUT
 * /api/v1/identity/agents/{id}/override with federationOverrides array.
 *
 * Reached from FederationInheritanceSection in IdentityAgentDetailPageV2.
 */
import React, { useEffect, useState } from 'react';
import { Save, X as XIcon, AlertTriangle, Trash2 } from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Pill } from '../Pill';
import {
  updateIdentityAgentOverride,
  resetIdentityAgentOverride,
  apiFetch,
} from '../../../services/api';

// Defined inline since api.js doesn't expose a federation-peers list
// helper yet — direct apiFetch call to the same endpoint the new
// Stage 3d-frontend page will use.
const listFederationPeers = () => apiFetch('/api/v1/identity/federation-peers');

export function AgentFederationOverrideSlideout({
  isOpen,
  onClose,
  onSaved,
  agentId,
  agentName,
  mode = 'add',
  initialPeerIds = [],
}) {
  const [availablePeers, setAvailablePeers] = useState([]);
  const [selectedPeerIds, setSelectedPeerIds] = useState(new Set(initialPeerIds));
  const [loadingPeers, setLoadingPeers] = useState(false);
  const [peersError, setPeersError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset on close.
  useEffect(() => {
    if (isOpen) return;
    setSelectedPeerIds(new Set()); setError(null); setPeersError(null);
  }, [isOpen]);

  // Sync initial selection when slideout opens.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedPeerIds(new Set(initialPeerIds || []));
  }, [isOpen, initialPeerIds]);

  // Load available peers when slideout opens.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingPeers(true);
    setPeersError(null);
    listFederationPeers().then((res) => {
      if (cancelled) return;
      if (res?.ok === false) {
        setPeersError(res.error || 'Could not load federation peers.');
        setLoadingPeers(false);
        return;
      }
      setAvailablePeers(Array.isArray(res?.data) ? res.data : []);
      setLoadingPeers(false);
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  const togglePeer = (peerId) => {
    setSelectedPeerIds((prev) => {
      const next = new Set(prev);
      if (next.has(peerId)) next.delete(peerId);
      else next.add(peerId);
      return next;
    });
  };

  const handleAdd = async () => {
    setError(null);
    if (selectedPeerIds.size === 0) {
      setError('Select at least one peer to install an override. To revert to inherit, use Remove.');
      return;
    }

    const body = {
      federationOverrides: Array.from(selectedPeerIds),
    };

    setSaving(true);
    const res = await updateIdentityAgentOverride(agentId, body);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Save failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(res?.data);
    onClose();
  };

  const handleRemove = async () => {
    setError(null);
    setSaving(true);
    const res = await resetIdentityAgentOverride(agentId);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Delete failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(null);
    onClose();
  };

  const isRemove = mode === 'remove';

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={520}
      ariaLabel={isRemove ? 'Remove agent federation override' : 'Install agent federation override'}
      header={
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            {isRemove ? 'Remove federation override' : 'Install federation override'}
          </h2>
          <Pill tone={isRemove ? 'danger' : 'trust'} size="sm">
            {isRemove ? 'Destructive' : 'Override'}
          </Pill>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" iconLeft={<XIcon size={13} />} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {isRemove ? (
            <Button variant="danger" size="sm" iconLeft={<Trash2 size={13} />}
                    onClick={handleRemove} disabled={saving}>
              {saving ? 'Removing…' : 'Remove override'}
            </Button>
          ) : (
            <Button variant="primary" size="sm" iconLeft={<Save size={13} />}
                    onClick={handleAdd} disabled={saving || loadingPeers}>
              {saving ? 'Saving…' : 'Install override'}
            </Button>
          )}
        </div>
      }
    >
      <div className="px-4 py-4 flex flex-col gap-4">
        {error && (
          <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-md px-3 py-2 text-[12.5px] text-v2-danger flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="text-[12.5px] text-v2-ink-secondary">
          Agent: <span className="text-v2-ink font-semibold">{agentName || agentId}</span>
        </div>

        {isRemove ? (
          <div className="bg-v2-warning-soft border border-v2-warning/30 rounded-v2-md px-3 py-2.5 text-[12.5px] text-v2-warning">
            Removing the override clears federation customisations on this
            agent's override row. The agent reverts to inheriting the federation
            peer set from instance / tenant / org defaults.
          </div>
        ) : (
          <>
            <div className="bg-v2-info-soft border border-v2-border-subtle rounded-v2-md px-3 py-2.5 text-[12px] text-v2-ink-secondary">
              Selected peers replace the resolved set this agent can attest
              into. Leave a peer unchecked to exclude it. Pick at least one.
            </div>

            {loadingPeers && (
              <div className="text-[12px] text-v2-ink-muted">Loading available peers…</div>
            )}
            {peersError && (
              <div className="text-[12px] text-v2-danger">
                Could not load peers: {peersError}
              </div>
            )}
            {!loadingPeers && !peersError && availablePeers.length === 0 && (
              <div className="bg-v2-warning-soft border border-v2-warning/30 rounded-v2-md px-3 py-2.5 text-[12.5px] text-v2-warning">
                No federation peers configured for this org yet. Configure peers
                under Identity → Federation peers before installing an agent-level
                override.
              </div>
            )}

            {availablePeers.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="text-[11px] uppercase tracking-wide text-v2-ink-muted font-semibold">
                  Available peers
                </div>
                {availablePeers.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-start gap-3 px-3 py-2 border border-v2-border-subtle rounded-v2-md cursor-pointer hover:bg-v2-muted/50">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={selectedPeerIds.has(p.id)}
                      onChange={() => togglePeer(p.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-v2-ink">
                        {p.name}
                      </div>
                      <div className="font-mono-v2 text-[11px] text-v2-ink-muted truncate">
                        {p.spiffeId}
                      </div>
                      <div className="text-[10.5px] text-v2-ink-muted mt-0.5">
                        {p.direction} · {p.isActive === false ? 'retired' : 'active'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Slideout>
  );
}

export default AgentFederationOverrideSlideout;
